import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Papa from 'papaparse';
import { useDropzone } from 'react-dropzone';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { useEnvironment } from 'src/hooks/environment';
import {
  useApplyBulkDeviceUpdate,
  useGetBulkUpdateTemplate,
  useRefreshAfterBulkDeviceUpdate,
  useValidateBulkDeviceUpdate,
} from 'src/hooks/device';
import { useTagOptions } from 'src/hooks/tag';
import { useDeploymentRings } from 'src/hooks/deploymentRing';
import { csvExportFileName, handleCsvFileDownload } from 'src/utils/download.util';
import { toCsv } from 'src/utils/csv';
import parseError from 'src/utils/parseError';
import { addRowContextToChunkError } from 'src/utils/bulkUpdateErrors';
import {
  COLUMNS,
  REFERENCE_ONLY_COLUMNS,
  chunkRows,
  toApiRow,
  toRow,
  validateBulkUpdateFile,
} from './bulkUpdateColumns';
import preValidateRows from './preValidateRows';

const ACCEPTED_FILES = {
  'text/csv': ['.csv'],
  'application/vnd.ms-excel': ['.csv'],
  'text/plain': ['.csv'],
};

const CONCURRENT_CHUNKS = 3;
const MAX_LISTED_ERRORS = 5;

// The wizard moves forward through these; each stage's result stays on screen under the next.
const PHASES = ['initial', 'parsed', 'preValidated', 'validating', 'validated', 'updating', 'done'];

const lowercaseFirstLetter = text => text.charAt(0).toLowerCase() + text.slice(1);

// The data keys come back camelCased from the column names ("Unique External Id" -> uniqueExternalId).
const cellFor = (row, column) => row?.[lowercaseFirstLetter(column.replace(/\s+/g, ''))];

const errorMessage = (error, chunk) => addRowContextToChunkError(parseError(error), chunk);

// Runs `send` for each chunk, a few at a time. Every chunk runs even when one fails, so all the
// problems are reported in one pass. Returns the settled outcomes in chunk order.
const sendChunks = async (chunks, send, onChunkDone) => {
  const outcomes = [];
  for (let start = 0; start < chunks.length; start += CONCURRENT_CHUNKS) {
    const batch = chunks.slice(start, start + CONCURRENT_CHUNKS);
    // eslint-disable-next-line no-await-in-loop
    const settled = await Promise.allSettled(
      batch.map(chunk => send(chunk).finally(() => onChunkDone())),
    );
    outcomes.push(...settled);
  }
  return outcomes;
};

const BulkUpdateModal = ({ open, query, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: environment } = useEnvironment();
  const fetchTemplate = useGetBulkUpdateTemplate();
  const validateMutation = useValidateBulkDeviceUpdate();
  const applyMutation = useApplyBulkDeviceUpdate();
  const refreshDevices = useRefreshAfterBulkDeviceUpdate();
  const { data: tagData, isLoading: isLoadingTags } = useTagOptions({
    search: '',
    enabled: open,
  });
  const { data: ringData, isLoading: isLoadingRings } = useDeploymentRings({ enabled: open });

  const [deviceCount, setDeviceCount] = useState(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  const [phase, setPhase] = useState('initial');
  const [uploadStatus, setUploadStatus] = useState(null); // null | 'uploading' | 'error'
  const [fileErrors, setFileErrors] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [preResults, setPreResults] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [updateResults, setUpdateResults] = useState([]);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [validationError, setValidationError] = useState('');
  const [updateError, setUpdateError] = useState('');

  const reached = stage => PHASES.indexOf(phase) >= PHASES.indexOf(stage);
  const isBusy = phase === 'validating' || phase === 'updating';

  const reset = useCallback(() => {
    setPhase('initial');
    setUploadStatus(null);
    setFileErrors([]);
    setHeaders([]);
    setRows([]);
    setPreResults([]);
    setValidationResults([]);
    setUpdateResults([]);
    setProgress({ completed: 0, total: 0 });
    setValidationError('');
    setUpdateError('');
  }, []);

  // The count shown is for the filter the list had when the modal opened.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    let cancelled = false;
    setDeviceCount(null);
    fetchTemplate(query, 0)
      .then(template => {
        if (!cancelled) setDeviceCount(template?.totalCount ?? null);
      })
      .catch(() => {
        if (!cancelled) setDeviceCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, query, fetchTemplate]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleDownloadTemplate = useCallback(async () => {
    setIsLoadingTemplate(true);
    try {
      const template = await fetchTemplate(query, -1);
      const columns = template?.columns || [];
      if (columns.length === 0) {
        enqueueSnackbar('Something went wrong.', { variant: 'error', autoHideDuration: 3000 });
        return;
      }
      const templateRows = (template.data || []).map(row =>
        columns.map(column => cellFor(row, column)),
      );
      handleCsvFileDownload(
        toCsv(columns, templateRows),
        csvExportFileName('device_update_template'),
      );
    } catch (error) {
      enqueueSnackbar('Something went wrong.', { variant: 'error', autoHideDuration: 3000 });
    } finally {
      setIsLoadingTemplate(false);
    }
  }, [enqueueSnackbar, fetchTemplate, query]);

  const handleFile = useCallback(
    file => {
      reset();
      setUploadStatus('uploading');
      Papa.parse(file, {
        header: false,
        skipEmptyLines: false,
        complete: results => {
          const [fileHeaders = [], ...rest] = results.data;
          // Row numbers are file lines (header is line 1), so blank lines are dropped only after numbering.
          const fileRows = rest
            .map((values, index) => ({ rowNumber: index + 2, values }))
            .filter(({ values }) => values.some(cell => cell && cell.trim() !== ''));
          const problems = validateBulkUpdateFile(fileHeaders, fileRows);
          if (problems.length > 0) {
            setFileErrors(problems);
            setUploadStatus('error');
            return;
          }
          setHeaders(fileHeaders);
          setRows(fileRows.map(({ rowNumber, values }) => toRow(fileHeaders, values, rowNumber)));
          setUploadStatus(null);
          setPhase('parsed');
        },
        error: error => {
          setFileErrors([`Error parsing CSV: ${error.message}`]);
          setUploadStatus('error');
        },
      });
    },
    [reset],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: files => {
      if (files.length > 0) handleFile(files[0]);
    },
    accept: ACCEPTED_FILES,
    maxFiles: 1,
  });

  const chunks = useMemo(() => chunkRows(rows), [rows]);

  const preErrorRowNumbers = useMemo(
    () => new Set(preResults.map(result => result.rowNumber)),
    [preResults],
  );
  const readyRows = useMemo(
    () => rows.filter(row => !preErrorRowNumbers.has(row._rowNumber)),
    [rows, preErrorRowNumbers],
  );

  const handlePreValidate = () => {
    setPreResults(
      preValidateRows(rows, {
        tagNames: (tagData?.tags || []).map(tag => tag.name),
        rings: ringData?.rings || [],
      }),
    );
    setPhase('preValidated');
  };

  const handleContinueValidation = async () => {
    const validationChunks = chunkRows(readyRows);
    setPhase('validating');
    setValidationError('');
    setProgress({ completed: 0, total: validationChunks.length });

    const outcomes = await sendChunks(
      validationChunks,
      chunk => validateMutation.mutateAsync({ devices: chunk.map(toApiRow) }),
      () => setProgress(previous => ({ ...previous, completed: previous.completed + 1 })),
    );

    const results = [];
    const failures = [];
    outcomes.forEach((outcome, index) => {
      if (outcome.status === 'fulfilled') {
        results.push(...outcome.value);
      } else {
        failures.push(errorMessage(outcome.reason, validationChunks[index]));
      }
    });

    if (failures.length > 0) {
      setValidationError([...new Set(failures)].join('\n\n'));
    } else {
      setValidationResults(results);
    }
    setPhase('validated');
  };

  const handleUpdate = async () => {
    const validIds = new Set(
      validationResults.filter(result => result.status === 'valid').map(result => result.idRef),
    );
    const updateChunks = chunkRows(readyRows.filter(row => validIds.has(row.uniqueExternalId)));
    setPhase('updating');
    setUpdateError('');
    setProgress({ completed: 0, total: updateChunks.length });

    const outcomes = await sendChunks(
      updateChunks,
      chunk => applyMutation.mutateAsync({ devices: chunk.map(toApiRow) }),
      () => setProgress(previous => ({ ...previous, completed: previous.completed + 1 })),
    );

    const results = [];
    const failures = [];
    outcomes.forEach((outcome, index) => {
      if (outcome.status === 'fulfilled') {
        results.push(...outcome.value);
      } else {
        failures.push(errorMessage(outcome.reason, updateChunks[index]));
      }
    });

    setUpdateResults(results);
    if (failures.length > 0) {
      setUpdateError([...new Set(failures)].join('\n\n'));
    }
    await refreshDevices();
    setPhase('done');
  };

  const validationErrorRows = validationResults.filter(result => result.status === 'error');
  const updatedCount = updateResults.filter(
    result => result.status === 'valid' && result.action === 'update',
  ).length;
  const updateErrorRows = updateResults.filter(result => result.status === 'error');
  const toUpdateCount = validationResults.filter(result => result.status === 'valid').length;
  const unchangedCount = validationResults.filter(result => result.status === 'unchanged').length;

  // Every problem found in any step, in file order, for the Download Invalid Rows file.
  const invalidRows = useMemo(() => {
    const byRowNumber = new Map();
    const add = (rowNumber, errors) => {
      byRowNumber.set(rowNumber, [...(byRowNumber.get(rowNumber) || []), ...errors]);
    };
    preResults.forEach(result => add(result.rowNumber, result.errors));
    const rowById = new Map(rows.map(row => [row.uniqueExternalId, row]));
    [...validationResults, ...updateResults]
      .filter(result => result.status === 'error')
      .forEach(result => {
        const row = rowById.get(result.idRef);
        if (row) add(row._rowNumber, result.errors);
      });
    return rows
      .filter(row => byRowNumber.has(row._rowNumber))
      .map(row => ({ row, errors: byRowNumber.get(row._rowNumber) }));
  }, [preResults, validationResults, updateResults, rows]);

  const handleDownloadInvalidRows = () => {
    const csv = toCsv(
      [...headers, 'Errors'],
      invalidRows.map(({ row, errors }) => [
        ...headers.map((_, index) => row._originalRow[index] || ''),
        errors.join('; '),
      ]),
    );
    handleCsvFileDownload(csv, csvExportFileName('device_update_errors'));
  };

  const startOverButton = (
    <Button variant="outlined" size="small" color="success" onClick={reset}>
      Start Over
    </Button>
  );

  const downloadInvalidRowsButton = invalidRows.length > 0 && (
    <Button
      variant="outlined"
      size="small"
      color="success"
      startIcon={<Iconify icon="eva:download-outline" />}
      onClick={handleDownloadInvalidRows}>
      Download Invalid Rows
    </Button>
  );

  const progressAlert = label => (
    <Alert severity="info">
      <Typography>
        {label} ({progress.completed}/{progress.total} chunks)
      </Typography>
      <LinearProgress
        variant="determinate"
        value={progress.total ? (progress.completed / progress.total) * 100 : 0}
        sx={{ mt: 1 }}
      />
    </Alert>
  );

  const errorList = (results, describe) => (
    <Box sx={{ mb: 2 }}>
      {results.slice(0, MAX_LISTED_ERRORS).map(result => (
        <Typography key={describe(result)} variant="body2" color="error">
          {describe(result)}
        </Typography>
      ))}
      {results.length > MAX_LISTED_ERRORS && (
        <Typography variant="body2">
          ...and {results.length - MAX_LISTED_ERRORS} more errors
        </Typography>
      )}
    </Box>
  );

  const isLoadingReferenceData = isLoadingTags || isLoadingRings;
  const isCurrent = stage => phase === stage;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'escapeKeyDown' && !isBusy) handleClose();
      }}
      maxWidth="lg"
      fullWidth>
      <DialogTitle>Bulk Update</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Typography variant="body1" color="text.secondary">
            Update Devices for {environment?.name || 'this environment'}.
          </Typography>

          {phase === 'initial' && !uploadStatus && (
            <>
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Download the template to see the required columns:
                </Typography>
                <Stack component="ul" spacing={0.5} sx={{ mt: 0, mb: 2, pl: 3 }}>
                  <Typography component="li" variant="body2">
                    <strong>{COLUMNS.uniqueExternalId}</strong> cannot be changed. It is used to
                    look up which device to update.
                  </Typography>
                  <Typography component="li" variant="body2">
                    <strong>{REFERENCE_ONLY_COLUMNS.join(' and ')}</strong> are for reference only.
                    They are ignored on upload and can be omitted.
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button
                    variant="outlined"
                    startIcon={<Iconify icon="eva:download-outline" />}
                    onClick={handleDownloadTemplate}
                    disabled={isLoadingTemplate}>
                    {isLoadingTemplate ? 'Generating...' : 'Download Update Template'}
                  </Button>
                  {deviceCount !== null && (
                    <Typography variant="body2" color="text.secondary">
                      {deviceCount} device(s) in the current filter
                    </Typography>
                  )}
                </Stack>
              </Box>
              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' },
                }}>
                <input {...getInputProps()} />
                <Typography color="text.secondary">
                  {isDragActive ? 'Drop CSV here' : 'Drop CSV here or click to browse'}
                </Typography>
              </Box>
            </>
          )}

          {uploadStatus === 'uploading' && <Alert severity="info">Parsing CSV file...</Alert>}

          {uploadStatus === 'error' && (
            <Alert severity="error">
              <Stack spacing={1} alignItems="flex-start">
                {fileErrors.map(message => (
                  <Typography key={message} variant="body2">
                    {message}
                  </Typography>
                ))}
                {startOverButton}
              </Stack>
            </Alert>
          )}

          {reached('parsed') && (
            <Alert severity="success">
              <Typography variant="h6">File Parsed</Typography>
              <Typography variant="body2">
                {rows.length} total row(s), {chunks.length} chunk(s)
              </Typography>
              {isCurrent('parsed') && (
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    onClick={handlePreValidate}
                    disabled={isLoadingReferenceData}>
                    Pre-Validate Data
                  </Button>
                  {startOverButton}
                </Stack>
              )}
            </Alert>
          )}

          {reached('preValidated') && (
            <Alert severity={preResults.length > 0 ? 'warning' : 'success'}>
              <Typography variant="h6">Pre-Validation Complete</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }}>
                {readyRows.length > 0 && (
                  <Chip label={`${readyRows.length} rows ready`} color="success" />
                )}
                {preResults.length > 0 && (
                  <Chip label={`${preResults.length} format errors`} color="error" />
                )}
              </Stack>
              {preResults.length > 0 && (
                <>
                  <Typography variant="subtitle2">Format errors found:</Typography>
                  {errorList(
                    preResults,
                    result =>
                      `Row ${result.rowNumber} (${result.idRef}): ${result.errors.join(', ')}`,
                  )}
                </>
              )}
              {isCurrent('preValidated') && (
                <Stack direction="row" spacing={2}>
                  {readyRows.length > 0 && (
                    <Button
                      variant="contained"
                      size="small"
                      color="success"
                      onClick={handleContinueValidation}>
                      Continue Validation (excludes rows with errors)
                    </Button>
                  )}
                  {startOverButton}
                  {downloadInvalidRowsButton}
                </Stack>
              )}
            </Alert>
          )}

          {phase === 'validating' && progressAlert('Validating with the server...')}

          {validationError && (
            <Alert severity="error">
              <Typography variant="h6">Validation Failed</Typography>
              <Typography>
                An error occurred during validation. If contacting support, please provide the
                following info:
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                {validationError}
              </Typography>
              {isCurrent('validated') && (
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  {startOverButton}
                  {downloadInvalidRowsButton}
                </Stack>
              )}
            </Alert>
          )}

          {reached('validated') && !validationError && (
            <Alert severity={validationErrorRows.length > 0 ? 'warning' : 'success'}>
              <Typography variant="h6">Full Validation Complete</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }}>
                {toUpdateCount > 0 && <Chip label={`${toUpdateCount} to update`} color="warning" />}
                {validationErrorRows.length > 0 && (
                  <Chip label={`${validationErrorRows.length} errors`} color="error" />
                )}
                {unchangedCount > 0 && <Chip label={`${unchangedCount} unchanged`} />}
              </Stack>
              {validationErrorRows.length > 0 && (
                <>
                  <Typography variant="subtitle2">Validation errors:</Typography>
                  {errorList(
                    validationErrorRows,
                    result => `${result.idRef}: ${result.errors.join(', ')}`,
                  )}
                </>
              )}
              {isCurrent('validated') && (
                <Stack direction="row" spacing={2}>
                  {toUpdateCount > 0 && (
                    <Button variant="contained" size="small" color="success" onClick={handleUpdate}>
                      Update Validated Device Records
                    </Button>
                  )}
                  {startOverButton}
                  {downloadInvalidRowsButton}
                </Stack>
              )}
            </Alert>
          )}

          {phase === 'updating' && progressAlert('Updating devices...')}

          {reached('done') && updateError && (
            <Alert severity="error">
              <Typography variant="h6">Update Failed</Typography>
              <Typography>
                {updatedCount} device(s) were updated before the error. If contacting support,
                please provide the following info:
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                {updateError}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                {startOverButton}
                {downloadInvalidRowsButton}
              </Stack>
            </Alert>
          )}

          {reached('done') && !updateError && (
            <Alert severity={updateErrorRows.length > 0 ? 'warning' : 'success'}>
              <Typography variant="h6">Update Complete!</Typography>
              <Typography variant="body2">
                {updatedCount} device(s) updated
                {updateErrorRows.length > 0 ? `, ${updateErrorRows.length} failed` : ''}.
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                {startOverButton}
                {downloadInvalidRowsButton}
              </Stack>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={isBusy}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

BulkUpdateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  query: PropTypes.shape({}).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default BulkUpdateModal;
